import React, { useState, useEffect } from 'react';
import COEHeader from './COEHeader';
import { useLocation } from 'react-router-dom';
import api from '../api';

/**
 * ExamPaperTemplateManager - Professional Redesign
 * All core logic preserved, UI/UX enhanced
 */

const ExamPaperTemplateManager = () => {
  const location = useLocation();
  const orgInfo = location.state?.orgInfo || {};
  const tenant = location.state?.tenant || {};
  const logoUrl = orgInfo.logoUrl || '/logo.svg';
  const tenantId = tenant?._id || tenant?.id || localStorage.getItem('tenantId') || '';

  // Academic context state
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programTypeId, setProgramTypeId] = useState('');
  const [programId, setProgramId] = useState('');
  const [academicContext, setAcademicContext] = useState({
    programTypeId: '',
    programId: '',
    programTypeName: '',
    programName: '',
  });
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [contextError, setContextError] = useState('');

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success', id: null });

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToast({ show: true, message, type, id });
    setTimeout(() => {
      setToast(prev => {
        if (prev.id === id) {
          return { show: false, message: '', type: 'success', id: null };
        }
        return prev;
      });
    }, 4000);
  };

  const isAnyModalOpen = createModalOpen || editModalOpen || previewModalOpen || deleteConfirmOpen;

  // Fetch program types
  useEffect(() => {
    if (!tenantId) return;
    setLoadingProgramTypes(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => {
        const types = res.data.programTypes || [];
        setProgramTypes(types);
      })
      .catch(() => setContextError('Failed to load program types'))
      .finally(() => setLoadingProgramTypes(false));
  }, [tenantId]);

  // Fetch programs when program type changes
  useEffect(() => {
    if (!tenantId || !programTypeId) {
      setPrograms([]);
      return;
    }
    setLoadingPrograms(true);
    api.get('/api/programs', { params: { tenantId, programTypeId, status: 'active' } })
      .then(res => {
        const progs = res.data.programs || [];
        setPrograms(progs);
      })
      .catch(() => setContextError('Failed to load programs'))
      .finally(() => setLoadingPrograms(false));
  }, [tenantId, programTypeId]);

  // Update academicContext
  useEffect(() => {
    if (programTypeId && programId) {
      const selectedProgramType = programTypes.find(pt => pt._id === programTypeId);
      const selectedProgram = programs.find(p => p._id === programId);
      setAcademicContext({
        programTypeId,
        programTypeName: selectedProgramType?.name || '',
        programId,
        programName: selectedProgram?.name || '',
      });
    }
  }, [programTypeId, programId, programTypes, programs]);

  // Fetch templates
  const fetchTemplates = React.useCallback(async () => {
    if (!programTypeId || !programId || !tenantId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const res = await api.get('/api/coe/exam-paper-template', {
        params: { programTypeId, programId }
      });
      setTemplates(res.data.data || []);
    } catch (err) {
      setTemplates([]);
      console.error('[fetchTemplates] error:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [programTypeId, programId, tenantId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || template.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getTemplateTypeMeta = (templateType) => {
    const type = templateType || 'both';
    if (type === 'cie_only') {
      return {
        label: 'CIE Only',
        classes: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    }
    if (type === 'end_exam_only') {
      return {
        label: 'End Exam Only',
        classes: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    }
    return {
      label: 'Both (CIE + End Exam)',
      classes: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
  };

  // Handle template creation
  const handleCreateTemplate = async (templateData) => {
    if (!programTypeId || !programId) {
      showToast('Please select program type and program.', 'error');
      return false;
    }

    const totalSectionMarks = templateData.sections.reduce((sum, sec) => sum + Number(sec.maxMarks || 0), 0);
    if (templateData.sections.some(sec => Number(sec.maxMarks) <= 0 || Number(sec.maxMarks) > Number(templateData.maxMarks))) {
      showToast(`Each section's marks must be greater than 0 and not exceed Max Marks (${templateData.maxMarks}).`, 'error');
      return false;
    }
    if (Number(totalSectionMarks) !== Number(templateData.maxMarks)) {
      showToast(`Sum of section marks (${totalSectionMarks}) must be exactly equal to Max Marks (${templateData.maxMarks}).`, 'error');
      return false;
    }

    const payload = {
      programTypeId,
      programId,
      templateType: templateData.templateType,
      name: templateData.name,
      maxMarks: Number(templateData.maxMarks),
      sections: templateData.sections.map(sec => ({
        name: sec.name,
        maxMarks: Number(sec.maxMarks),
        description: sec.description || ''
      })),
      allowFacultyPortalUpdate: templateData.allowFacultyPortalUpdate,
      visibleToFaculty: templateData.visibleToFaculty,
      allowExternalExaminerView: templateData.allowExternalExaminerView,
      enforceCOPOMapping: templateData.enforceCOPOMapping,
    };

    try {
      await api.post('/api/coe/exam-paper-template', payload);
      showToast('Template created successfully!', 'success');
      setCreateModalOpen(false);
      fetchTemplates();
      return true;
    } catch (err) {
      showToast('Failed to create template: ' + (err?.response?.data?.message || err.message), 'error');
      return false;
    }
  };

  // Handle template update
  const handleUpdateTemplate = async (templateData) => {
    if (!templateData._id) {
      showToast('Template ID is missing. Cannot update this template.', 'error');
      return false;
    }
    const totalSectionMarks = templateData.sections.reduce((sum, sec) => sum + Number(sec.maxMarks || 0), 0);
    if (templateData.sections.some(sec => Number(sec.maxMarks) <= 0 || Number(sec.maxMarks) > Number(templateData.maxMarks))) {
      showToast(`Each section's marks must be greater than 0 and not exceed Max Marks (${templateData.maxMarks}).`, 'error');
      return false;
    }
    if (Number(totalSectionMarks) !== Number(templateData.maxMarks)) {
      showToast(`Sum of section marks (${totalSectionMarks}) must be exactly equal to Max Marks (${templateData.maxMarks}).`, 'error');
      return false;
    }

    const payload = {
      templateId: templateData._id,
      templateType: templateData.templateType,
      name: templateData.name,
      maxMarks: Number(templateData.maxMarks),
      sections: templateData.sections.map(sec => ({
        name: sec.name,
        maxMarks: Number(sec.maxMarks),
        description: sec.description || ''
      })),
      allowFacultyPortalUpdate: templateData.allowFacultyPortalUpdate,
      visibleToFaculty: templateData.visibleToFaculty,
      allowExternalExaminerView: templateData.allowExternalExaminerView,
      enforceCOPOMapping: templateData.enforceCOPOMapping,
      programTypeId: academicContext.programTypeId,
      programId: academicContext.programId,
    };

    try {
      await api.post('/api/coe/exam-paper-template', payload);
      showToast('Template updated successfully!', 'success');
      setEditModalOpen(false);
      setSelectedTemplate(null);
      fetchTemplates();
      return true;
    } catch (err) {
      showToast('Failed to update template: ' + (err?.response?.data?.message || err.message), 'error');
      return false;
    }
  };

  // Handle template deletion
  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await api.delete(`/api/coe/exam-paper-template/${templateToDelete._id}`);
      showToast('Template deleted successfully!', 'success');
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (err) {
      showToast('Failed to delete template: ' + (err?.response?.data?.message || err.message), 'error');
    }
  };

  // Handle status change
  const handleStatusChange = async (templateId, newStatus, event) => {
    event.stopPropagation();
    try {
      await api.patch(`/api/coe/exam-paper-template/${templateId}/status`, { status: newStatus });
      showToast(`Template status updated to ${newStatus}.`, 'success');
      fetchTemplates();
    } catch (err) {
      showToast('Failed to update status: ' + (err?.response?.data?.message || err.message), 'error');
    }
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditModalOpen(true);
  };

  const handleDeleteConfirm = (template) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const isContextSelected = academicContext.programTypeId && academicContext.programId;

  return (
    <>
      {/* Professional Toast Notification */}
      {toast.show && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in">
          <div className={`rounded-xl shadow-lg p-4 min-w-[320px] flex items-start gap-3 border ${
            toast.type === 'success' 
              ? 'bg-emerald-50 border-emerald-200' 
              : toast.type === 'error'
              ? 'bg-rose-50 border-rose-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              toast.type === 'success' ? 'bg-emerald-100' : 'bg-rose-100'
            }`}>
              {toast.type === 'success' ? (
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-emerald-800' : 'text-rose-800'
              }`}>
                {toast.type === 'success' ? 'Success' : 'Error'}
              </p>
              <p className={`text-xs mt-0.5 ${
                toast.type === 'success' ? 'text-emerald-600' : 'text-rose-600'
              }`}>
                {toast.message}
              </p>
            </div>
            <button 
              onClick={() => setToast({ show: false, message: '', type: 'success', id: null })}
              className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Global overlay */}
      {isAnyModalOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fade-in" aria-hidden="true" />
      )}

      <div className={`min-h-screen w-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 ${isAnyModalOpen ? 'overflow-hidden' : ''}`}>
        <div className={isAnyModalOpen ? 'pointer-events-none opacity-60' : ''}>
          <COEHeader orgInfo={orgInfo} tenant={tenant} />
        </div>

        <main className={`flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 ${isAnyModalOpen ? 'pointer-events-none' : ''}`}>
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent">
                  Exam Paper Templates
                </h1>
                <p className="text-slate-500 mt-2 text-sm">
                  Design and manage standardized assessment structures for your programs
                </p>
              </div>
              <div className="flex gap-2">
                <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors group">
                  <svg className="w-5 h-5 text-slate-400 group-hover:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Context Selector Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Select Academic Context</h3>
                  <p className="text-xs text-slate-500">Choose program type and program to manage templates</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Program Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</span>
                    Program Type
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={programTypeId}
                      onChange={e => setProgramTypeId(e.target.value)}
                      disabled={loadingProgramTypes}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer hover:border-blue-300 disabled:bg-slate-50 disabled:cursor-wait text-slate-700"
                    >
                      <option value="">Select Program Type</option>
                      {programTypes.map(pt => (
                        <option key={pt._id} value={pt._id}>{pt.name}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      {loadingProgramTypes ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>

                {/* Program */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">2</span>
                    Program
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={programId}
                      onChange={e => setProgramId(e.target.value)}
                      disabled={!programTypeId || loadingPrograms}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed hover:border-blue-300 text-slate-700"
                    >
                      <option value="">Select Program</option>
                      {programs.map(p => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                      {loadingPrograms ? (
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {contextError && (
                <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <p className="text-rose-600 text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {contextError}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          {!isContextSelected ? (
            /* No Context Selected State */
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mb-5">
                <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">No Context Selected</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Please select a program type and program above to view or create exam paper templates.
              </p>
            </div>
          ) : (
            <>
              {/* Context Summary Bar */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-4 mb-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-4 text-white">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-medium">{academicContext.programName || '—'}</span>
                    </div>
                    <div className="text-xs opacity-75">•</div>
                    <div className="text-sm opacity-90">{academicContext.programTypeName}</div>
                  </div>
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 text-sm font-semibold rounded-lg transition-all hover:shadow-md hover:scale-105"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Template
                  </button>
                </div>
              </div>

              {/* Search and Filter Bar */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Templates Grid */}
              {loadingTemplates ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-pulse">
                      <div className="p-5">
                        <div className="h-5 bg-slate-200 rounded-lg w-3/4 mb-3"></div>
                        <div className="h-4 bg-slate-100 rounded w-1/2 mb-4"></div>
                        <div className="space-y-2">
                          <div className="h-3 bg-slate-100 rounded w-full"></div>
                          <div className="h-3 bg-slate-100 rounded w-5/6"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mb-5">
                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">No Templates Found</h3>
                  <p className="text-slate-500 max-w-md mx-auto mb-6">
                    {searchTerm || statusFilter !== 'all' 
                      ? "No templates match your search criteria. Try adjusting your filters."
                      : "No exam paper templates have been created for this academic context yet."}
                  </p>
                  {(searchTerm || statusFilter !== 'all') ? (
                    <button
                      onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                    >
                      Clear Filters
                    </button>
                  ) : (
                    <button
                      onClick={() => setCreateModalOpen(true)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Your First Template
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTemplates.map((template) => {
                    const typeMeta = getTemplateTypeMeta(template.templateType);
                    return (
                    <div
                      key={template._id}
                      className="group bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1"
                    >
                      {/* Status Selector - Professional Dropdown */}
                      <div className="absolute right-3 top-3 z-10">
                        <select
                          value={template.status || 'inactive'}
                          onChange={(e) => handleStatusChange(template._id, e.target.value, e)}
                          onClick={(e) => e.stopPropagation()}
                          className={`text-xs font-medium px-2.5 py-1 rounded-full border focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer transition-all ${
                            template.status === 'active' 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' 
                              : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
                          }`}
                        >
                          <option value="active" className="bg-white text-slate-700">● Active</option>
                          <option value="inactive" className="bg-white text-slate-700">○ Inactive</option>
                        </select>
                      </div>

                      {/* Card Header */}
                      <div className="p-5 pb-3">
                        <h3 className="font-semibold text-slate-800 pr-20 line-clamp-1">{template.name}</h3>
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${typeMeta.classes}`}>
                            {typeMeta.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                            Max: {template.maxMarks} marks
                          </span>
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs text-slate-500">{template.sections.length} sections</span>
                          </div>
                        </div>
                      </div>

                      {/* Marks Distribution Bar */}
                      <div className="px-5 pb-3">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Marks Distribution</span>
                          <span>{template.maxMarks} total</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
                          {template.sections.map((section, i) => (
                            <div 
                              key={i}
                              className="h-full bg-gradient-to-r from-blue-400 to-blue-600"
                              style={{ width: `${(section.maxMarks / template.maxMarks) * 100}%` }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Sections Preview */}
                      <div className="px-5 pb-3">
                        <div className="space-y-1.5">
                          {template.sections.slice(0, 2).map((section, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm">
                              <span className="text-slate-600 truncate flex-1">{section.name}</span>
                              <span className="text-slate-400 text-xs ml-2">{section.maxMarks} marks</span>
                            </div>
                          ))}
                          {template.sections.length > 2 && (
                            <p className="text-xs text-blue-500 mt-1">+{template.sections.length - 2} more sections</p>
                          )}
                        </div>
                      </div>

                      {/* Flags */}
                      <div className="px-5 pb-2 flex flex-wrap gap-1.5">
                        {template.allowFacultyPortalUpdate && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Faculty Edit
                          </span>
                        )}
                        {template.visibleToFaculty && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Visible
                          </span>
                        )}
                        {template.allowExternalExaminerView && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Ext. Examiner
                          </span>
                        )}
                      </div>

                      {/* Card Actions */}
                      <div className="border-t border-slate-100 bg-slate-50/30 p-3 flex justify-end gap-2">
                        <ActionButton 
                          icon="preview" 
                          tooltip="Preview" 
                          onClick={() => handlePreview(template)} 
                        />
                        <ActionButton 
                          icon="edit" 
                          tooltip="Edit" 
                          onClick={() => handleEdit(template)} 
                        />
                        <ActionButton 
                          icon="delete" 
                          tooltip="Delete" 
                          onClick={() => handleDeleteConfirm(template)} 
                          variant="danger"
                        />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>

        {/* Modals */}
        {createModalOpen && (
          <Modal title="Create Exam Paper Template" onClose={() => setCreateModalOpen(false)} maxWidth="2xl">
            <TemplateForm
              mode="create"
              onSubmit={handleCreateTemplate}
              onCancel={() => setCreateModalOpen(false)}
              academicContext={academicContext}
            />
          </Modal>
        )}

        {editModalOpen && selectedTemplate && (
          <Modal title="Edit Exam Paper Template" onClose={() => { setEditModalOpen(false); setSelectedTemplate(null); }} maxWidth="2xl">
            <TemplateForm
              mode="edit"
              initialData={selectedTemplate}
              onSubmit={handleUpdateTemplate}
              onCancel={() => { setEditModalOpen(false); setSelectedTemplate(null); }}
              academicContext={academicContext}
            />
          </Modal>
        )}

        {previewModalOpen && selectedTemplate && (
          <Modal title="" onClose={() => { setPreviewModalOpen(false); setSelectedTemplate(null); }} maxWidth="3xl" hideHeader>
            <ExamPaperPreview 
              template={selectedTemplate} 
              academicContext={academicContext} 
              orgInfo={{...orgInfo, logoUrl}} 
              tenant={tenant} 
              onClose={() => { setPreviewModalOpen(false); setSelectedTemplate(null); }} 
            />
          </Modal>
        )}

        {deleteConfirmOpen && (
          <Modal title="Delete Template" onClose={() => { setDeleteConfirmOpen(false); setTemplateToDelete(null); }} maxWidth="sm">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-rose-100 to-rose-200 rounded-2xl flex items-center justify-center mb-5">
                <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Confirm Deletion</h3>
              <p className="text-slate-500 mb-6">
                Are you sure you want to delete "<span className="font-semibold text-slate-700">{templateToDelete?.name}</span>"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteTemplate}
                  className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-semibold transition-all"
                >
                  Delete
                </button>
                <button
                  onClick={() => { setDeleteConfirmOpen(false); setTemplateToDelete(null); }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

// Action Button Component
const ActionButton = ({ icon, tooltip, onClick, variant = 'default' }) => {
  const iconMap = {
    preview: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    edit: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    delete: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    )
  };

  const variantClasses = {
    default: 'text-slate-500 hover:text-blue-600 hover:bg-blue-50',
    danger: 'text-slate-500 hover:text-rose-600 hover:bg-rose-50'
  };

  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg transition-all ${variantClasses[variant]} group relative`}
      title={tooltip}
    >
      {iconMap[icon]}
      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
        {tooltip}
      </span>
    </button>
  );
};

// Modal Component
const Modal = ({ children, title, onClose, maxWidth = 'md', hideHeader = false }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-hidden">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidthClasses[maxWidth]} my-8 transform transition-all relative z-50 animate-fade-in`}>
        {!hideHeader && (
          <div className="flex justify-between items-center px-6 py-5 border-b border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
            <button 
              onClick={onClose} 
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="p-6 max-h-[calc(100vh-120px)] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// Template Form Component
const TemplateForm = ({ mode, initialData, onSubmit, onCancel, academicContext }) => {
  const [formData, setFormData] = useState({
    _id: initialData?._id || '',
    name: initialData?.name || '',
    templateType: initialData?.templateType || 'both',
    maxMarks: initialData?.maxMarks || '',
    sections: initialData?.sections?.map(s => ({ ...s, maxMarks: s.maxMarks })) || [{ name: '', maxMarks: '', description: '' }],
    allowFacultyPortalUpdate: initialData?.allowFacultyPortalUpdate || false,
    visibleToFaculty: initialData?.visibleToFaculty || false,
    allowExternalExaminerView: initialData?.allowExternalExaminerView || false,
    enforceCOPOMapping: initialData?.enforceCOPOMapping || false,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSectionChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, { name: '', maxMarks: '', description: '' }]
    }));
  };

  const removeSection = (idx) => {
    if (formData.sections.length === 1) return;
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== idx)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.maxMarks || formData.maxMarks <= 0) newErrors.maxMarks = 'Valid max marks is required';
    if (formData.sections.some(s => !s.name.trim())) newErrors.sections = 'All sections must have a name';
    if (formData.sections.some(s => !s.maxMarks || s.maxMarks <= 0)) newErrors.sections = 'All sections must have valid marks';
    
    const totalMarks = formData.sections.reduce((sum, s) => sum + Number(s.maxMarks || 0), 0);
    if (Number(totalMarks) !== Number(formData.maxMarks)) {
      newErrors.sections = `Sum of section marks (${totalMarks}) must equal Max Marks (${formData.maxMarks})`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  const totalMarks = formData.sections.reduce((sum, s) => sum + Number(s.maxMarks || 0), 0);
  const marksBalance = formData.maxMarks ? Number(totalMarks) - Number(formData.maxMarks) : 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Template Name *</label>
          <input
            type="text"
            className={`w-full border ${errors.name ? 'border-rose-300 focus:ring-rose-500' : 'border-slate-200 focus:ring-blue-500'} rounded-xl px-4 py-2.5 focus:ring-2 focus:border-transparent outline-none transition-all bg-slate-50 focus:bg-white`}
            placeholder="e.g., Final Examination - Semester 1"
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
          />
          {errors.name && <p className="text-rose-500 text-xs mt-1 flex items-center gap-1"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Maximum Marks *</label>
          <input
            type="number"
            min="0"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-slate-50 focus:bg-white"
            placeholder="e.g., 100"
            value={formData.maxMarks}
            onChange={e => handleChange('maxMarks', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Template Type *</label>
          <select
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-slate-50 focus:bg-white"
            value={formData.templateType}
            onChange={e => handleChange('templateType', e.target.value)}
          >
            <option value="both">Both (CIE + End Exam)</option>
            <option value="cie_only">CIE Only</option>
            <option value="end_exam_only">End Exam Only</option>
          </select>
        </div>
      </div>

      {/* Context Display */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Academic Context (Fixed)</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-slate-500">Program Type:</span> <span className="font-medium text-slate-700 ml-2">{academicContext.programTypeName || '—'}</span></div>
          <div><span className="text-slate-500">Program:</span> <span className="font-medium text-slate-700 ml-2">{academicContext.programName || '—'}</span></div>
        </div>
        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          Template will be associated with the selected program type and program
        </p>
      </div>

      {/* Sections */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-sm font-semibold text-slate-700">Sections *</label>
          <button type="button" onClick={addSection} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Section
          </button>
        </div>
        
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {formData.sections.map((section, idx) => (
            <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-blue-200 transition-colors">
              <div className="flex flex-col sm:flex-row gap-3 mb-3">
                <input
                  type="text"
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                  placeholder="Section Name (e.g., Part A)"
                  value={section.name}
                  onChange={e => handleSectionChange(idx, 'name', e.target.value)}
                />
                <input
                  type="number"
                  min="0"
                  className="w-full sm:w-28 border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                  placeholder="Marks"
                  value={section.maxMarks}
                  onChange={e => handleSectionChange(idx, 'maxMarks', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeSection(idx)}
                  disabled={formData.sections.length === 1}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${formData.sections.length === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-rose-600 hover:bg-rose-50'}`}
                >
                  Remove
                </button>
              </div>
              <input
                type="text"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                placeholder="Description (optional)"
                value={section.description || ''}
                onChange={e => handleSectionChange(idx, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>
        
        {/* Marks Balance Indicator */}
        {formData.maxMarks && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-600">Total Section Marks:</span>
              <span className={`font-semibold ${marksBalance === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {totalMarks} / {formData.maxMarks} marks
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${marksBalance === 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${(totalMarks / formData.maxMarks) * 100}%` }}
              />
            </div>
            {marksBalance !== 0 && (
              <p className="text-xs text-amber-600 mt-2">
                {marksBalance > 0 ? `Over by ${marksBalance} marks` : `Under by ${Math.abs(marksBalance)} marks`}
              </p>
            )}
          </div>
        )}
        
        {errors.sections && <p className="text-rose-500 text-xs mt-2 flex items-center gap-1"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>{errors.sections}</p>}
      </div>

      {/* Permissions */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-3">Workflow Permissions</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.allowFacultyPortalUpdate}
              onChange={e => handleChange('allowFacultyPortalUpdate', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Allow Faculty Portal Update</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.visibleToFaculty}
              onChange={e => handleChange('visibleToFaculty', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Visible to Faculty</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.allowExternalExaminerView}
              onChange={e => handleChange('allowExternalExaminerView', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Allow External Examiner View</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.enforceCOPOMapping}
              onChange={e => handleChange('enforceCOPOMapping', e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Enforce CO/PO Mapping</span>
          </label>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-slate-200">
        <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold transition-all shadow-sm">
          {mode === 'create' ? 'Create Template' : 'Save Changes'}
        </button>
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Exam Paper Preview Component - With Logo on Left Side
const ExamPaperPreview = ({ template, academicContext, orgInfo, tenant, onClose }) => {
  const totalMarks = template.sections.reduce((sum, s) => sum + Number(s.maxMarks), 0);
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="max-h-[calc(100vh-140px)] overflow-y-auto">
      {/* Exam Paper Header with Logo on Left */}
      <div className="border-b-2 border-slate-200 pb-6 mb-6">
        <div className="flex items-center justify-between">
          {/* Logo on Left */}
          <div className="flex-shrink-0">
            {(orgInfo?.logo || tenant?.logo || orgInfo?.logoUrl) && (
              <img 
                src={orgInfo?.logo || tenant?.logo || orgInfo?.logoUrl} 
                alt="Institution Logo" 
                className="h-16 w-auto object-contain"
              />
            )}
          </div>
          
          {/* Institution Info Center */}
          <div className="flex-1 text-center px-4">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
              {orgInfo?.name || tenant?.name || 'Institution Name'}
            </h1>
            <p className="text-slate-600 text-sm mt-1 font-medium">
              {academicContext.programName}
            </p>
            <p className="text-slate-400 text-xs mt-2">{currentDate}</p>
          </div>
          
          {/* Empty div for balance */}
          <div className="w-16"></div>
        </div>
      </div>

      {/* Template Details & Flags */}
      <div className="mb-8 flex flex-wrap gap-4 justify-center">
        <div className="flex flex-col items-center">
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${template.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {template.status === 'active' ? 'Active' : 'Inactive'}
          </span>
          <span className="text-xs text-slate-500 mt-1">Template Status</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Faculty Edit
          </span>
          <span className="text-xs text-slate-500 mt-1">Faculty can edit template</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Visible
          </span>
          <span className="text-xs text-slate-500 mt-1">Visible to faculty</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Ext. Examiner
          </span>
          <span className="text-xs text-slate-500 mt-1">External access</span>
        </div>
      </div>

      {/* Exam Title */}
      <div className="mb-8 text-center">
        <h2 className="text-xl font-bold text-blue-700">{template.name}</h2>
        <div className="inline-flex items-center gap-2 mt-2 px-4 py-1.5 bg-slate-100 rounded-full">
          <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-sm font-medium text-slate-600">Maximum Marks: {template.maxMarks}</span>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4 mb-8">
        {template.sections.map((section, idx) => (
          <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-blue-50 to-white px-5 py-3 border-b border-slate-200">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-blue-700">{section.name}</h3>
                <span className="text-sm font-semibold text-slate-600 bg-white px-3 py-1 rounded-full shadow-sm">
                  {section.maxMarks} marks
                </span>
              </div>
              {section.description && (
                <p className="text-xs text-slate-500 mt-1.5">{section.description}</p>
              )}
            </div>
            <div className="p-5 bg-white">
              <p className="text-slate-400 text-sm italic flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                [Questions will be populated here based on the question paper template]
              </p>
              <div className="mt-4 space-y-2">
                <div className="text-sm text-slate-600 border-b border-dashed border-slate-100 pb-2 flex justify-between">
                  <span className="font-medium">Sample Question 1:</span>
                  <span className="text-slate-400 text-xs">(___ marks)</span>
                </div>
                <div className="text-sm text-slate-600 border-b border-dashed border-slate-100 pb-2 flex justify-between">
                  <span className="font-medium">Sample Question 2:</span>
                  <span className="text-slate-400 text-xs">(___ marks)</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Total Marks Footer */}
      <div className="mt-6 pt-4 border-t-2 border-slate-200 text-right">
        <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg">
          <span className="text-sm font-medium text-slate-600">Total Marks:</span>
          <span className="text-xl font-bold text-blue-700">{totalMarks}</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 text-center text-xs text-slate-400 border-t border-slate-100">
        <p>This is a system-generated exam paper template. Questions need to be added separately.</p>
      </div>

      {/* Close Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-medium transition-all shadow-sm"
        >
          Close Preview
        </button>
      </div>
    </div>
  );
};

export default ExamPaperTemplateManager;